// UpdateAvailableBanner — one-line prompt when a newer deploy is live.
//
// Mounted once in Layout. Never reloads on its own (a user may be mid-form or
// on a softphone call — during a live call the banner stays hidden entirely).
// "Later" snoozes it for 30 minutes; the check keeps running so it comes back.
// See lib/versionCheck.ts for why this exists.

import { useState } from "react";
import { useUpdateAvailable } from "../lib/versionCheck";
import { useSoftphone } from "../context/SoftphoneContext";

const SNOOZE_MS = 30 * 60_000;
const CALL_BUSY = new Set(["connecting", "ringing", "in-call", "ending"]);

export default function UpdateAvailableBanner() {
  const { updateAvailable } = useUpdateAvailable();
  const softphone = useSoftphone();
  const [snoozedAt, setSnoozedAt] = useState<number | null>(null);

  if (!updateAvailable) return null;
  if (CALL_BUSY.has(String(softphone.status))) return null;
  if (snoozedAt && Date.now() - snoozedAt < SNOOZE_MS) return null;

  return (
    <div
      role="status"
      className="fixed left-1/2 -translate-x-1/2 bottom-20 sm:bottom-6 z-[60] max-w-[calc(100vw-2rem)] w-[26rem] rounded-xl shadow-lg border border-slate-700 bg-slate-900 text-white px-4 py-3 flex items-center gap-3"
    >
      <span className="text-lg flex-shrink-0" aria-hidden>🔄</span>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold">Portal updated</div>
        <div className="text-xs text-slate-300">Reload to get the latest version — this tab is running an older one.</div>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="px-3 py-1.5 text-xs font-semibold rounded bg-bardin-orange text-white hover:bg-bardin-orange/90 flex-shrink-0"
      >
        Reload
      </button>
      <button
        type="button"
        onClick={() => setSnoozedAt(Date.now())}
        className="text-xs text-slate-300 hover:text-white flex-shrink-0"
        aria-label="Remind me later"
      >
        Later
      </button>
    </div>
  );
}
